import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { createTestimonial } from './testimonialsApi';
import type { ConsentScope, PromptKey } from './types';

const PROMPTS: Array<{ key: PromptKey; label: string }> = [
  { key: 'can_do_now', label: "Describe one thing you can do now that you couldn't before." },
  { key: 'click_moment', label: 'Was there a moment when something finally clicked? What made it click?' },
  { key: 'to_a_friend', label: 'What would you tell a friend who is nervous about taking chemistry?' },
];

const SCOPES: Array<{ key: ConsentScope; label: string; help: string }> = [
  { key: 'internal', label: 'Internal', help: 'The instructor and program staff only.' },
  { key: 'marketing', label: 'Marketing', help: 'May be quoted anonymously in course/program materials.' },
  { key: 'named_public', label: 'Named public', help: 'May be quoted with my first name and last initial.' },
];

const BODY_MAX = 1500;

/** A single testimonial submission form. */
export default function TestimonialForm({ onDone }: { onDone?: () => void } = {}) {
  const [promptKey, setPromptKey] = useState<PromptKey | null>(null);
  const [body, setBody] = useState('');
  const [scope, setScope] = useState<ConsentScope>('internal');
  const [displayNameOk, setDisplayNameOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div className="p-2 text-center">
        <p className="text-sm font-medium text-ws-ink">Thank you — your testimonial was submitted.</p>
        <p className="mt-1 text-xs text-ws-ink-3">
          You can review or withdraw consent any time from{' '}
          <Link to="/me/testimonials" className="font-medium text-ws-accent-ink hover:underline">My testimonials</Link>.
        </p>
        {onDone && <Button size="sm" variant="secondary" className="mt-4" onClick={onDone}>Close</Button>}
      </div>
    );
  }

  const submit = async () => {
    if (!promptKey || !body.trim()) {
      setError('Pick a prompt and write a few sentences first.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createTestimonial({
        prompt_key: promptKey,
        body: body.trim(),
        consent_scope: scope,
        display_name_ok: scope === 'named_public' && displayNameOk,
      });
      setDone(true);
    } catch {
      setError('Could not submit your testimonial. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-ws-ink">Write a testimonial</h3>
      <p className="mt-1 text-xs text-ws-ink-3">Optional, and separate from the survey.</p>

      <fieldset className="mt-4 space-y-1.5">
        <legend className="mb-1 text-sm font-medium text-ws-ink">Pick a prompt</legend>
        {PROMPTS.map((p) => (
          <label key={p.key} className="flex cursor-pointer items-start gap-2 text-sm text-ws-ink-2">
            <input type="radio" name="testimonial-prompt" className="mt-0.5" checked={promptKey === p.key} onChange={() => setPromptKey(p.key)} />
            <span>{p.label}</span>
          </label>
        ))}
      </fieldset>

      {promptKey && (
        <div className="mt-4">
          <textarea
            rows={4}
            maxLength={BODY_MAX}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Your answer…"
            className="w-full rounded-lg border border-ws-border bg-ws-surface p-2.5 text-sm text-ws-ink outline-none focus:border-ws-accent"
          />
          <div className="mt-0.5 text-right text-xs text-ws-ink-4">{body.length}/{BODY_MAX}</div>
        </div>
      )}

      <fieldset className="mt-4 space-y-2">
        <legend className="mb-1 text-sm font-medium text-ws-ink">Who can see this?</legend>
        {SCOPES.map((s) => (
          <label key={s.key} className="flex cursor-pointer items-start gap-2 text-sm text-ws-ink-2">
            <input type="radio" name="testimonial-scope" className="mt-0.5" checked={scope === s.key} onChange={() => setScope(s.key)} />
            <span>
              <span className="font-medium text-ws-ink">{s.label}</span> — {s.help}
            </span>
          </label>
        ))}
      </fieldset>

      <label className={`mt-2 flex items-center gap-2 text-sm ${scope === 'named_public' ? 'text-ws-ink-2' : 'text-ws-ink-4'}`}>
        <input
          type="checkbox"
          disabled={scope !== 'named_public'}
          checked={scope === 'named_public' && displayNameOk}
          onChange={(e) => setDisplayNameOk(e.target.checked)}
        />
        Show my first name and last initial with this quote.
      </label>

      <p className="mt-4 text-xs text-ws-ink-4">
        You can withdraw or narrow this at any time from{' '}
        <Link to="/me/testimonials" className="font-medium text-ws-ink-3 underline">My testimonials</Link>; anything
        already printed cannot be recalled, anything digital will be taken down.
      </p>

      {error && <p className="mt-2 text-sm text-ws-danger">{error}</p>}

      <div className="mt-4 flex justify-end gap-2">
        {onDone && <Button variant="ghost" size="sm" onClick={onDone}>Cancel</Button>}
        <Button size="sm" loading={submitting} onClick={submit}>Submit testimonial</Button>
      </div>
    </div>
  );
}
